import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { CircularProgress, Typography } from '@mui/material';

import Management from './member';
import Timeline from './Timeline';

import './Sidebar.css';

const Container = styled.div`

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0px 80px 0px;
    position: relative;
    z-index: 1;
    overflow: hidden; /* Ensure no scroll bars */

    /* Bokeh background effect */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle, rgba(255, 0, 0, 0.3), transparent 40%) 0% 0% / 10px 10px,
                    radial-gradient(circle, rgba(0, 0, 255, 0.3), transparent 40%) 50px 50px / 20px 20px,
                    radial-gradient(circle, rgba(255, 0, 0, 0.3), transparent 40%) 100px 100px / 30px 30px;
        animation: moveBokeh 20s linear infinite;
        z-index: -1;
        filter: blur(15px);
    }

    @keyframes moveBokeh {
        0% { background-position: 0% 0%, 50px 50px, 100px 100px; }
        50% { background-position: 100% 100%, 0px 50px, 50px 0px; }
        100% { background-position: 0% 0%, 50px 50px, 100px 100px; }
    }

    @media (max-width: 960px) {
        padding: 0px;
        margin: 0%;
    }
`;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center; /* Center the content */
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 960px;
    padding: 80px 0;
    gap: 12px;
    @media (max-width: 960px) {
        padding: 20px 0;
    }
`;

const Wrapperdesc = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 1200px;
    padding: 40px 0 20px;
    gap: 12px;
    @media (max-width: 768px) {
        width: 90%;
        padding: 20px 0 10px;
    }
`;

const Title = styled.div`
    font-family: 'Poppins', sans-serif;
    font-size: 3rem;
    text-align: center;
    font-weight: 700;
    margin-top: 10vh;
    color: ${({ theme }) => theme.text_primary};
    letter-spacing: -0.02em;
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 2rem;
    }
`;

const Desc = styled.div`
    margin-top: 2%;
    font-family: 'Inter', sans-serif;
    font-size: 1.125rem;
    line-height: 1.75;
    text-align: justify;
    color: rgba(255, 255, 255, 0.85);
    max-width: 1100px;
    white-space: pre-line;
    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 1rem;
        line-height: 1.6;
    }
`;



const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    flex-direction: column;
`;

const Sidebar = ({ isEnglish, toggleLanguage }) => {
    const [isExpanded, setIsExpanded] = useState(false); // State for sidebar expansion

    useEffect(() => {
        // Dynamically append Font Awesome link to the head
        const fontAwesomeLink = document.createElement("link");
        fontAwesomeLink.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css";
        fontAwesomeLink.rel = "stylesheet";
        document.head.appendChild(fontAwesomeLink);

        // Cleanup function to remove the link when the component unmounts
        return () => {
            document.head.removeChild(fontAwesomeLink);
        };
    }, []);

    const toggleSidebar = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`sidebar ${isExpanded ? 'expanded' : ''}`}>
            <div className="toggle-button" onClick={toggleSidebar}>
                {isExpanded ? (
                    <i className="fas fa-chevron-left"></i>
                ) : (
                    <div className='globe'>
                        <i className="fas fa-globe" id='globe'></i>
                    </div>
                )}
            </div>
            {isExpanded && (
                <div className="language-toggle">
                    <div
                        className={`language-option ${isEnglish ? 'selected' : ''}`}
                        onClick={() => toggleLanguage(true)}
                    >
                        ENG
                    </div>
                    <div
                        className={`language-option ${!isEnglish ? 'selected' : ''}`}
                        onClick={() => toggleLanguage(false)}
                    >
                        ಕನ್ನಡ
                    </div>
                </div>
            )}
        </div>
    );
};

const Index = () => {
    const [isEnglish, setIsEnglish] = useState(true);
    const [, setExperiences] = useState([]);

    const toggleLanguage = (isEnglish) => {
        setIsEnglish(isEnglish);
    };

    const originDesc = isEnglish 
        ? "Souharda Co-operative Society was established on August 19, 2020, under the Karnataka Souharda Act, 1997—a progressive law that encourages cooperative development based on self-help, mutual support, and social responsibility.\nFounded by Mr. Santiag T. Horta, our society was officially inaugurated on December 4, 2020, in Kelaginor, Honavar, Karnataka.\nUnder the leadership of President Mr. Thomas T. Horta and Vice President Mr. James J. Rodrigues, we are dedicated to uplifting our members through transparent, people-centric cooperative practices.\nOur mission is simple yet powerful: to enhance the well-being of our community by fostering cooperation, trust, and sustainable development. As a proud part of the Karnataka Souharda movement, we work to empower individuals, encourage financial growth, and create opportunities that benefit every member.\nTogether, we strive to build a stronger, more prosperous community—one where collective progress leads to shared success. Join us as we grow, support, and empower each other!"
        : "ಸೌಹಾರ್ದ ಸಹಕಾರಿ ಸಂಘವು ಆಗಸ್ಟ್ 19, 2020 ರಂದು 'ಕರ್ನಾಟಕ ಸೌಹಾರ್ದ ಕಾಯ್ದೆ, 1997' ರ ಅಡಿಯಲ್ಲಿ ಸ್ಥಾಪನೆಯಾಯಿತು. ಈ ಕಾಯ್ದೆಯು ಸ್ವಸಹಾಯ, ಪರಸ್ಪರ ಬೆಂಬಲ ಮತ್ತು ಸಾಮಾಜಿಕ ಜವಾಬ್ದಾರಿಯ ಆಧಾರದ ಮೇಲೆ ಸಹಕಾರಿ ಅಭಿವೃದ್ಧಿಯನ್ನು ಉತ್ತೇಜಿಸುವ ಪ್ರಗತಿಪರ ಕಾನೂನಾಗಿದೆ.\nಶ್ರೀ ಸಾಂತಿಯಾಗ್ ಟಿ. ಹೋರ್ಟಾ ಅವರಿಂದ ಸ್ಥಾಪಿಸಲ್ಪಟ್ಟ ನಮ್ಮ ಸಂಘವು, ಡಿಸೆಂಬರ್ 4, 2020 ರಂದು ಕರ್ನಾಟಕದ ಹೊನ್ನಾವರದ ಕೆಳಗಿನೂರಿನಲ್ಲಿ ಅಧಿಕೃತವಾಗಿ ಉದ್ಘಾಟನೆಗೊಂಡಿತು.\nಅಧ್ಯಕ್ಷರಾದ ಶ್ರೀ ಥಾಮಸ್ ಟಿ. ಹೋರ್ಟಾ ಮತ್ತು ಉಪಾಧ್ಯಕ್ಷರಾದ ಶ್ರೀ ಜೇಮ್ಸ್ ಜೆ. ರೊಡ್ರಿಗಸ್ ಅವರ ನೇತೃತ್ವದಲ್ಲಿ, ಪಾರದರ್ಶಕ ಮತ್ತು ಜನಸ್ನೇಹಿ ಸಹಕಾರಿ ತತ್ವಗಳ ಮೂಲಕ ನಮ್ಮ ಸದಸ್ಯರ ಏಳಿಗೆಗಾಗಿ ನಾವು ಶ್ರಮಿಸುತ್ತಿದ್ದೇವೆ.\nನಮ್ಮ ಧ್ಯೇಯ: ಸಹಕಾರ, ನಂಬಿಕೆ ಮತ್ತು ಸುಸ್ಥಿರ ಅಭಿವೃದ್ಧಿಯನ್ನು ಬೆಳೆಸುವ ಮೂಲಕ ನಮ್ಮ ಸಮುದಾಯದ ಯೋಗಕ್ಷೇಮವನ್ನು ಹೆಚ್ಚಿಸುವುದು ನಮ್ಮ ಸರಳ ಮತ್ತು ಪ್ರಬಲವಾದ ಧ್ಯೇಯವಾಗಿದೆ. ಕರ್ನಾಟಕ ಸೌಹಾರ್ದ ಚಳುವಳಿಯ ಹೆಮ್ಮೆಯ ಭಾಗವಾಗಿ, ನಾವು ವ್ಯಕ್ತಿಗಳನ್ನು ಸಬಲೀಕರಣಗೊಳಿಸಲು, ಆರ್ಥಿಕ ಬೆಳವಣಿಗೆಯನ್ನು ಉತ್ತೇಜಿಸಲು ಮತ್ತು ಪ್ರತಿ ಸದಸ್ಯರಿಗೂ ಪ್ರಯೋಜನವಾಗುವಂತಹ ಅವಕಾಶಗಳನ್ನು ಸೃಷ್ಟಿಸಲು ಕೆಲಸ ಮಾಡುತ್ತಿದ್ದೇವೆ.\nಸಾಮೂಹಿಕ ಪ್ರಗತಿಯು ಹಂಚಿಕೊಂಡ ಯಶಸ್ಸಿಗೆ ದಾರಿಯಾಗುವಂತಹ ಬಲವಾದ ಮತ್ತು ಹೆಚ್ಚು ಸಮೃದ್ಧ ಸಮುದಾಯವನ್ನು ನಿರ್ಮಿಸಲು ನಾವೆಲ್ಲರೂ ಒಗ್ಗೂಡಿ ಶ್ರಮಿಸೋಣ. ನಾವು ಬೆಳೆಯುತ್ತಾ, ಪರಸ್ಪರ ಬೆಂಬಲಿಸುತ್ತಾ ಮತ್ತು ಸಬಲೀಕರಣಗೊಳ್ಳುತ್ತಾ ಸಾಗುವ ಈ ಪಯಣದಲ್ಲಿ ನಮ್ಮೊಂದಿಗೆ ಕೈಜೋಡಿಸಿ!";

    return (
        <Container id="experience">
            <Wrapperdesc>
                <Title>{isEnglish ? 'Our Origin' : 'ನಮ್ಮ ಹಿನ್ನೆಲೆ'}</Title>
                <Desc>{originDesc}</Desc>
            </Wrapperdesc>

            <Timeline />

            <Wrapper>
                <Management />
            </Wrapper>
            
            <Sidebar isEnglish={isEnglish} toggleLanguage={toggleLanguage} />
        </Container>
    );
};

export default Index;
